import React from "react";
import { Box, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon/FuseSvgIcon";
import { CommentItem } from "./OrderDetailUIHelpers";



export const useIsMobile = () => {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("sm"));
};


export const useDialogProps = (DraggablePaper) => {
  const isMobile = useIsMobile();
  return isMobile
    ? { fullScreen: true }
    : {
        PaperComponent: DraggablePaper,
        "aria-labelledby": "draggable-dialog-title",
      };
};



export const accordionSummarySx = {
  "& .MuiAccordionSummary-content": { minWidth: 0, my: 1 },
};

/** Number badge + wrapping row of buttons / meta text. */
export const AbSummary = ({ index, children }) => (
  <Box
    sx={{
      width: "100%",
      minWidth: 0,
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      columnGap: { xs: 0.5, sm: 2 },
      rowGap: 0.5,
      py: 0.5,
      "& .MuiButton-root": { whiteSpace: "nowrap" },
    }}
  >
    <Box
      sx={{
        bgcolor: "secondary.main",
        color: "secondary.contrastText",
        borderRadius: "50%",
        width: 30,
        height: 30,
        lineHeight: "30px",
        textAlign: "center",
        fontSize: 14,
        fontWeight: 700,
        flexShrink: 0,
        mr: { xs: 0.5, sm: 0 },
      }}
    >
      {index + 1}
    </Box>
    {children}
  </Box>
);

/** "AB Received Date: 12-03-2025 - 10:30 GST" */
export const AbMeta = ({ label, value }) =>
  value ? (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ whiteSpace: { sm: "nowrap" } }}
    >
      {label}:{" "}
      <Box component="span" sx={{ color: "text.primary", fontWeight: 500 }}>
        {value}
      </Box>
    </Typography>
  ) : null;



export const CommentThread = ({ comments, token, allUserList }) => (
  <Box sx={{ maxHeight: { xs: 300, md: 420 }, overflowY: "auto", pr: 0.5 }}>
    {comments?.map((c) => {
      const u = allUserList?.find((v) => v.email === c.user);
      return (
        <CommentItem
          key={c.time}
          isOwn={c.user === token?.email}
          initial={c.user?.charAt(0)}
          name={u ? `${u.firstName} ${u.lastName}` : c.user}
          source={c.orderCommentSource}
          time={c.time}
          text={c.comment}
        />
      );
    })}
  </Box>
);



export const AnnotationPanel = ({ title, date, children }) => (
  <Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1,
        pb: 1,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {date && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            color: "text.secondary",
          }}
        >
          <FuseSvgIcon size={16} sx={{ color: "secondary.main" }}>
            heroicons-outline:calendar
          </FuseSvgIcon>
          <Typography variant="caption">{date}</Typography>
        </Box>
      )}
    </Box>
    {children}
  </Box>
);


export const GridToolbar = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1,
      mb: 0.5,
    }}
  >
    {children}
  </Box>
);

export const ActionGroup = ({ children }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 0.5,
      ml: "auto",
    }}
  >
    {children}
  </Box>
);

/** Replaces <div style={{height:300,width:"100%"}} className="relative">. */
export const AmendmentGridBox = ({ children }) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      height: { xs: 320, md: 300 },
      overflowX: "auto",
      WebkitOverflowScrolling: "touch",
      border: (t) => `1px solid ${t.palette.divider}`,
      borderRadius: 1,
    }}
  >
    {/* columns keep a readable width on phones; the box scrolls sideways */}
    <Box sx={{ minWidth: { xs: 760, md: 0 }, height: "100%" }}>{children}</Box>
  </Box>
);


export const FooterActions = ({ left, children }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      alignItems: { xs: "stretch", md: "center" },
      justifyContent: "space-between",
      gap: 1.5,
      mt: 2,
    }}
  >
    {left && <Box sx={{ "& .MuiButton-root": { width: { xs: "100%", md: "auto" } } }}>{left}</Box>}

    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        flexWrap: "wrap",
        alignItems: { xs: "stretch", sm: "center" },
        justifyContent: { sm: "flex-end" },
        gap: 1,
        flex: 1,
        "& .MuiButton-root": {
          m: 0,
          width: { xs: "100%", sm: "auto" },
        },
        "& > div": { width: { xs: "100%", sm: "auto" } },
      }}
    >
      {children}
    </Box>
  </Box>
);